import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Calculator, Building2, HelpCircle, ArrowRight, BookOpen } from 'lucide-react';

export default function HelpCenterPage() {
  const navigate = useNavigate();

  const helpTopics = [
    {
      icon: FileText,
      title: 'Tax Filing Help',
      description: 'Get answers to common tax filing questions',
      features: ['How to file your 1040', 'Understanding deductions', 'What documents you need', 'Tracking your refund']
    },
    {
      icon: Building2,
      title: 'LLC Formation Help',
      description: 'Learn about starting your business',
      features: ['Choosing the right state', 'Understanding EINs', 'Operating agreements', 'Ongoing compliance']
    },
    {
      icon: Calculator,
      title: 'Tax Calculator Help',
      description: 'Make the most of our free tools',
      features: ['Using the tax estimator', 'Refund calculations', 'Deduction finder tips', 'Understanding your results']
    },
    {
      icon: BookOpen,
      title: 'General Questions',
      description: 'Everything else you need to know',
      features: ['Account setup', 'Payment options', 'Security & privacy', 'Filing deadlines']
    }
  ];

  const faqs = [
    {
      question: 'How much does it cost to file?',
      answer: 'Start for free and only pay when you file. Our pricing is transparent with no hidden fees.'
    },
    {
      question: 'Is my information secure?',
      answer: 'Yes. We use bank-level encryption to protect your data and never sell your information.'
    },
    {
      question: 'When will I get my refund?',
      answer: 'Most refunds are issued within 21 days of acceptance by the IRS when you e-file.'
    },
    {
      question: 'Can I amend a previous return?',
      answer: 'Yes. We can help you file an amended return if you need to correct a previous filing.'
    }
  ];

  return (
    <PublicLayout>
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">Help Center</h1>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Find answers, get help, and learn how to make the most of Kosha Tax
          </p>
        </div>

        {/* Help Topics Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {helpTopics.map((topic, index) => {
            const Icon = topic.icon;
            return (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{topic.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">{topic.description}</p>
                  <ul className="space-y-2">
                    {topic.features.map((feature, idx) => (
                      <li key={idx} className="text-sm flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQs Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <HelpCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{faq.question}</h3>
                      <p className="text-gray-600 dark:text-gray-400">{faq.answer}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-blue-50 dark:bg-blue-900/20 rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Contact our support team and we'll get back to you right away.
          </p>
          <Button size="lg" onClick={() => navigate('/contact')}>
            Get Help <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </PublicLayout>
  );
}
